// /dnsResolver.js
export class CustomDNSResolver {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.testMode = false;
  }

  enableTestMode() {
    this.testMode = true;
    console.log("🧪 DNS Test Mode ENABLED - will resolve local IPs for testing");
  }

  disableTestMode() {
    this.testMode = false;
    console.log("🧪 DNS Test Mode DISABLED");
  }

  getDnsSettings() {
    const settings = localStorage.getItem("dnsSettings");
    return settings ? JSON.parse(settings) : { type: "system" };
  }

  isCustomDnsEnabled() {
    const settings = this.getDnsSettings();
    const enabled = settings.type === "custom" && settings.customDns;
    console.log("🔍 Custom DNS enabled check:", enabled, settings);
    return enabled;
  }

  shouldResolveDomain(domain) {
    // In test mode, allow local IPs and any IPs to be "resolved" for testing
    if (this.testMode) {
      console.log(`🧪 Test Mode: Will attempt to resolve ${domain} (including IPs)`);
      return true;
    }

    // Skip localhost and local development domains
    if (
      domain === "localhost" ||
      domain === "127.0.0.1" ||
      domain.startsWith("192.168.") ||
      domain.startsWith("10.") ||
      domain.startsWith("172.")
    ) {
      console.log(`🏠 Skipping local domain: ${domain}`);
      return false;
    }

    // In normal mode, skip if already an IP address (but allow in test mode)
    if (/^\d+\.\d+\.\d+\.\d+$/.test(domain)) {
      console.log(`🔢 Skipping IP address in normal mode: ${domain}`);
      return false;
    }

    return true;
  }

  async mockDnsResolution(domain) {
    console.log(`🧪 MOCK: Simulating DNS resolution for ${domain}`);

    // Simulate some delay
    await new Promise(resolve => setTimeout(resolve, 100));

    // Return a fake IP for testing - use different IPs for different domains
    const mockIps = {
      "google.com": "8.8.8.8",
      "cloudflare.com": "1.1.1.1",
      "github.com": "140.82.114.4",
      "188.95.89.3": "1.1.1.1", // Transform your problematic IP to Cloudflare
      "192.168.1.48": "93.184.216.34", // Mock your local IP
      localhost: "127.0.0.1",
      default: "93.184.216.34", // example.com IP
    };

    const mockIp = mockIps[domain] || mockIps.default;
    console.log(`🧪 MOCK: Resolved ${domain} to ${mockIp}`);
    return mockIp;
  }

  buildDoHUrl(domain) {
    const settings = this.getDnsSettings();
    if (!settings.customDns) return null;

    const { primary1, primary2, primary3, primary4 } = settings.customDns;
    const dnsServer = `${primary1}.${primary2}.${primary3}.${primary4}`;

    if (dnsServer === "8.8.8.8" || dnsServer === "8.8.4.4") {
      return `https://dns.google/resolve?name=${domain}&type=A`;
    } else if (dnsServer === "1.1.1.1" || dnsServer === "1.0.0.1") {
      return `https://cloudflare-dns.com/dns-query?name=${domain}&type=A`;
    } else {
      console.log(`Using Cloudflare DoH for custom DNS ${dnsServer}`);
      return `https://cloudflare-dns.com/dns-query?name=${domain}&type=A`;
    }
  }

  async resolveDomain(domain) {
    console.log(`🔍 resolveDomain called for: ${domain}`);

    if (!this.isCustomDnsEnabled()) {
      console.log("❌ Custom DNS not enabled, returning null");
      return null;
    }

    if (!this.shouldResolveDomain(domain)) {
      console.log(`❌ Should not resolve domain: ${domain}, returning null`);
      return null;
    }

    console.log(`✅ Proceeding with DNS resolution for: ${domain}`);

    // Check cache first
    const cacheKey = domain;
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      console.log(`💾 DNS Cache hit for ${domain}: ${cached.ip}`);
      return cached.ip;
    }

    try {
      let ip;

      // In test mode, use mock resolution
      if (this.testMode) {
        console.log(`🧪 Using mock resolution for: ${domain}`);
        ip = await this.mockDnsResolution(domain);
      } else {
        console.log(`🌐 Using real DNS resolution for: ${domain}`);
        const dohUrl = this.buildDoHUrl(domain);
        if (!dohUrl) {
          console.log("❌ No DoH URL available");
          return null;
        }

        console.log(`🔍 Resolving ${domain} using: ${dohUrl}`);

        const response = await fetch(dohUrl, {
          headers: {
            Accept: "application/dns-json",
          },
        });

        const data = await response.json();

        if (data.Answer && data.Answer.length > 0) {
          ip = data.Answer.find(answer => answer.type === 1)?.data;
          console.log(`✅ Real DNS resolved ${domain} to ${ip}`);
        }
      }

      if (ip) {
        // Cache the result
        this.cache.set(cacheKey, { ip, timestamp: Date.now() });
        console.log(`💾 Cached DNS result: ${domain} → ${ip}`);
        return ip;
      }

      console.warn(`❌ Could not resolve ${domain}`);
      return null;
    } catch (error) {
      console.error(`❌ DNS resolution failed for ${domain}:`, error);
      return null;
    }
  }

  async transformUrl(url) {
    console.log(`🌐 transformUrl called with: ${url}`);

    if (!this.isCustomDnsEnabled()) {
      console.log("📡 Custom DNS disabled, returning original URL");
      return url;
    }

    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;

      console.log(`🌐 Extracting domain from URL: ${domain}`);

      // Always try to resolve in test mode, or if it's a real domain
      const shouldTryResolve = this.testMode || this.shouldResolveDomain(domain);
      console.log(`🤔 Should try to resolve ${domain}? ${shouldTryResolve}`);

      if (!shouldTryResolve) {
        console.log(`🏠 Skipping DNS resolution for ${domain}`);
        return url;
      }

      console.log(`🔍 Attempting DNS resolution for: ${domain}`);
      const ip = await this.resolveDomain(domain);
      console.log(`📋 DNS resolution result for ${domain}: ${ip}`);

      if (ip && ip !== domain) {
        const originalDomain = urlObj.hostname;
        urlObj.hostname = ip;
        const transformedUrl = urlObj.toString();

        console.log(`🔧 DNS Transform SUCCESS:`);
        console.log(`   Original: ${url}`);
        console.log(`   Transformed: ${transformedUrl}`);
        console.log(`   Host header: ${originalDomain}`);

        return {
          url: transformedUrl,
          originalHost: originalDomain,
          originalUrl: url,
        };
      }

      console.log(`🔧 DNS Transform FAILED or no change needed, returning original URL`);
      return url;
    } catch (error) {
      console.error("❌ transformUrl failed:", error);
      return url;
    }
  }

  getDnsInfo() {
    const settings = this.getDnsSettings();
    const enabled = this.isCustomDnsEnabled();

    return {
      enabled,
      testMode: this.testMode,
      type: settings.type,
      customDns: enabled
        ? `${settings.customDns.primary1}.${settings.customDns.primary2}.${settings.customDns.primary3}.${settings.customDns.primary4}`
        : null,
      cacheSize: this.cache.size,
    };
  }
}

export const dnsResolver = new CustomDNSResolver();
window.dnsResolver = dnsResolver;
